'use client'

import { P2 } from '@/frontend/design-system/typography/Paragraph'
import { cn } from '@/frontend/utils'
import 'katex/dist/katex.min.css'
import { MDXRemote, type MDXRemoteSerializeResult } from 'next-mdx-remote'
import { serialize } from 'next-mdx-remote/serialize'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import type { TreeData } from '../../types'
import {
  AttachmentLinksList,
  type DbAttachment,
  mapAttachmentsToFiles
} from '../MarkdownEditor/AttachmentsManager'
import { createCustomPlugin } from './_utils/customPlugin'
import { ErrorBoundary } from './_utils/MdxErrorBoundary'
import { mdxComponents } from './mdx-components'

type PreviewProps = {
  content: { markdown: string; attachments?: DbAttachment[] }
  onScroll?: () => void
  onMouseEnter?: () => void
  treeDataRef?: React.RefObject<TreeData | null>
  onReady?: () => void
  className?: string
}

export const Preview = forwardRef<HTMLDivElement, PreviewProps>(
  ({ content, onScroll, onMouseEnter, treeDataRef, onReady, className }, ref) => {
    const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult | null>(null)
    const [hasError, setHasError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const lastScrollRef = useRef<number>(0)

    // save last scroll position
    const handleScroll = useCallback(() => {
      if (!hasError && ref && 'current' in ref && ref.current) {
        lastScrollRef.current = ref.current.scrollTop
      }
      onScroll?.()
    }, [hasError, onScroll, ref])

    const processMdx = useCallback(
      async (mdxContent: string) => {
        try {
          const customPlugin = createCustomPlugin(treeDataRef, () => {
            // restore last scroll position
            if (!hasError && ref && 'current' in ref && ref.current) {
              ref.current.scrollTop = lastScrollRef.current
            }
          })
          // TODO: add <component> normalization
          const serialized = await serialize(mdxContent, {
            mdxOptions: {
              remarkPlugins: [remarkMath, remarkGfm],
              rehypePlugins: [
                [
                  rehypeKatex,
                  {
                    strict: false,
                    output: 'html',
                    throwOnError: false,
                    trust: true,
                    displayMode: false
                  }
                ],
                customPlugin
              ]
            },
            parseFrontmatter: false
          })

          setMdxSource(serialized)
          setHasError(false)
          setErrorMessage('')
          onReady?.()
        } catch (error) {
          console.warn('MDX parse error:', error)
          setHasError(true)
          setErrorMessage(error instanceof Error ? error.message : 'Nieznany błąd składni')
        }
      },
      [ref, treeDataRef, hasError, onReady]
    )

    // process content when it changes
    useEffect(() => {
      void processMdx(content.markdown)
    }, [content, processMdx])

    return (
      <>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: preview panel uses scroll/mouseEnter for sync scrolling, not interactive content */}
        <div
          ref={ref}
          className={cn(className, 'custom-scrollbar h-full flex-1 overflow-auto py-1')}
          onScroll={handleScroll}
          onMouseEnter={onMouseEnter}
        >
          {hasError && (
            <P2 className='text-contentRed'>
              Nie udało się przetworzyć podglądu. Sprawdź poprawność składni lub komponentów.
              <div className='h-2' />
              {errorMessage}
            </P2>
          )}
          {!hasError && mdxSource && (
            <ErrorBoundary
              fallback={
                <P2 className='text-contentRed'>
                  Wystąpił błąd podczas renderowania. Upewnij się, że wszystkie użyte komponenty są
                  dostępne w edytorze.
                </P2>
              }
            >
              <div className='space-y-6 text-contentSecondary'>
                <MDXRemote
                  {...mdxSource}
                  components={mdxComponents as Parameters<typeof MDXRemote>[0]['components']}
                />
              </div>
            </ErrorBoundary>
          )}
        </div>
        {/* preview only attachments */}
        {content.attachments && (
          <div className='mt-4'>
            <AttachmentLinksList files={mapAttachmentsToFiles(content.attachments)} />
          </div>
        )}
      </>
    )
  }
)
Preview.displayName = 'Preview'
